import { useEffect, useRef, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Paperclip, Send, CheckCheck, LogOut, Bell, Settings as Gear } from "lucide-react";
import { api, errMsg, openFile, fmtTime } from "@/lib/api";
import { Mark7, Wordmark, LiveDot } from "@/components/brand";
import { toast } from "sonner";

export function AppHeader({ tabs, base, onLogout, extra, testid }) {
  return (
    <header className="bg-ink text-white sticky top-0 z-40" data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to={base} className="flex items-center gap-3"><Mark7 size={34} /><span className="hidden sm:inline"><Wordmark dark /></span></Link>
        <div className="flex items-center gap-2 sm:gap-4">
          <LiveDot light className="hidden md:inline-flex" />
          {extra}
          <button onClick={onLogout} className="min-h-[44px] px-2 flex items-center gap-2 f-mono text-[10px] tracking-[0.16em] uppercase text-white/70 hover:text-gold" data-testid="sign-out-btn"><LogOut className="w-4 h-4" /><span className="hidden sm:inline">Sign out</span></button>
        </div>
      </div>
      <nav className="max-w-7xl mx-auto px-4 lg:px-8 flex gap-6 overflow-x-auto no-scrollbar text-[13px] font-medium" aria-label="Sections">
        {tabs.map(([label, to, badge]) => (
          <NavLink key={to} to={to} end={to === base} className={({ isActive }) => `tab-link ${isActive ? "active" : ""}`} data-testid={`tab-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {label}{badge ? <span className="ml-1.5 bg-red7b text-white text-[10px] px-1.5 py-0.5 f-mono">{badge}</span> : null}
          </NavLink>
        ))}
      </nav>
      <div className="tri-line" />
    </header>
  );
}

export function BellLink({ to, count }) {
  return (
    <Link to={to} className="relative w-11 h-11 flex items-center justify-center hover:text-gold" aria-label={`Notifications, ${count} unread`} data-testid="notifications-bell">
      <Bell className="w-5 h-5" />
      {count > 0 && <span className="absolute top-1.5 right-1 min-w-[18px] h-[18px] px-1 bg-red7b text-white text-[10px] f-mono flex items-center justify-center rounded-full" data-testid="bell-count">{count}</span>}
    </Link>
  );
}

export function GearLink({ to }) {
  return <Link to={to} className="w-11 h-11 flex items-center justify-center hover:text-gold" aria-label="Settings" data-testid="settings-gear"><Gear className="w-5 h-5" /></Link>;
}

export function Thread({ messages, me, onSend, filePrefix, onCall, readOnly }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !files.length) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("text", text);
    files.forEach((f) => fd.append("files", f));
    try { await onSend(fd); setText(""); setFiles([]); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-ink flex flex-col h-[560px] max-h-[75vh]" data-testid="message-thread">
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.length === 0 && <p className="text-center text-white/50 text-sm py-10">No messages yet. Say hello.</p>}
        {messages.map((m) => {
          const mine = me === "client" ? m.from === "client" : m.from !== "client";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] px-4 py-3 ${me === "client" ? (mine ? "bg-gold text-ink" : "bg-white text-ink") : (mine ? "bg-white text-ink" : "bg-gold text-ink")}`} data-testid="message-bubble">
                {m.text && <p className="text-[15px] whitespace-pre-wrap break-words">{m.text}</p>}
                {m.attachments?.map((a) => <button key={a.id} onClick={() => openFile(`${filePrefix}/${a.id}`)} className="flex items-center gap-1.5 text-xs underline mt-1"><Paperclip className="w-3 h-3" />{a.filename || "Attachment"}</button>)}
                <p className="f-mono text-[9px] tracking-[0.1em] opacity-60 mt-1.5 flex items-center gap-1 justify-end">{fmtTime(m.createdAt)}{mine && m.readAt && <><CheckCheck className="w-3 h-3" /> Read</>}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      {!readOnly && (
        <form onSubmit={send} className="border-t border-white/10 p-3 flex flex-col gap-2">
          {files.length > 0 && <p className="text-xs text-white/70">{files.map((f) => f.name).join(", ")}</p>}
          <div className="flex gap-2 items-end">
            <label className="w-11 h-11 flex items-center justify-center text-white/70 hover:text-gold cursor-pointer shrink-0" aria-label="Attach file">
              <Paperclip className="w-5 h-5" /><input type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={(e) => setFiles([...e.target.files].slice(0, 4))} data-testid="message-attach-input" />
            </label>
            <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 2000))} rows={1} placeholder="Write a message…" className="flex-1 min-h-[44px] max-h-32 bg-white/5 border border-white/15 text-white p-3 text-[15px] focus:outline-none focus:border-gold resize-none" data-testid="message-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} />
            <button type="submit" disabled={busy} className="btn btn-gold !px-4 shrink-0" aria-label="Send" data-testid="message-send-btn"><Send className="w-4 h-4" /></button>
          </div>
          <div className="flex justify-between items-center">
            <span className="f-mono text-[9px] text-white/40">{text.length}/2000</span>
            {onCall && <button type="button" onClick={onCall} className="btn btn-red btn-sm !min-h-[36px]" data-testid="thread-request-call-btn">Request a Call</button>}
          </div>
        </form>
      )}
    </div>
  );
}

export function useLoad(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = async () => {
    try { setData((await api.get(path)).data); setError(null); } catch (e) { setError(e); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [path, ...deps]);
  return { data, error, reload, setData };
}
